/**
 * FILE: logger.service.ts
 * CHỨC NĂNG: Service logger trung tâm, bật/tắt theo môi trường.
 *
 * - Dev: log đầy đủ ra console (bật = true).
 * - Production production: tự động tắt (bật = false).
 * - Ở production build, Angular `define` option trong angular.json thay thế
 * `console.debug` bằng no-op ((() => {})) để loại bỏ debug log khỏi bundle.
 * - `console.error` luôn chạy kể cả production (quan trọng cho monitoring).
 *
 * Usage:
 * const logger = inject(LoggerService);
 * logger.log('Extension check:', result);
 * logger.warn('Cảnh báo...');
 * logger.error('Lỗi:', error);
 */
import {Injectable, isDevMode} from '@angular/core';

@Injectable({providedIn: 'root'})
export class LoggerService {
  /**
   * true: log ra console (dev).
   * false: im lặng (production).
   * Dùng isDevMode() của Angular để tự động phân biệt môi trường.
   * Có thể ghi đè thủ công nếu cần debug ở production.
   */
  public enabled = isDevMode();

  // ─── Methods chính ──────────────────────────────────────────────────────

  log(...args: any[]): void {
    if (this.enabled) {
      console.log(...args);
    }
  }

  warn(...args: any[]): void {
    if (this.enabled) {
      console.warn(...args);
    }
  }

  error(...args: any[]): void {
    // error luôn log — kể cả production — vì cần bắt lỗi quan trọng
    console.error(...args);
  }

  info(...args: any[]): void {
    if (this.enabled) {
      console.info(...args);
    }
  }

  debug(...args: any[]): void {
    if (this.enabled) {
      console.debug(...args);
    }
  }

  /**
   * Ghi log có nhãn — format: [LABEL] message
   */
  labeled(label: string, ...args: any[]): void {
    if (this.enabled) {
      console.log(`[${label}]`, ...args);
    }
  }

  /**
   * Ghi log theo nhóm — dùng console.group
   */
  group(label: string, ...args: any[]): void {
    if (this.enabled) {
      console.group(label);
      args.forEach(arg => console.log(arg));
      console.groupEnd();
    }
  }
}
